import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const emptyWork = () => ({ company: "", title: "", start: "", end: "", summary: "" });
const emptyEdu = () => ({ school: "", degree: "", year: "" });

export default function OnlineApplicationForm({ employerId, jobId, onSuccess }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    skills: "",
    why_good_fit: "",
    available_start: "",
    authorized_to_work: true,
  });
  const [work, setWork] = useState([emptyWork()]);
  const [edu, setEdu] = useState([emptyEdu()]);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const updateWork = (i, k) => (e) =>
    setWork((p) => p.map((w, idx) => (idx === i ? { ...w, [k]: e.target.value } : w)));
  const updateEdu = (i, k) => (e) =>
    setEdu((p) => p.map((w, idx) => (idx === i ? { ...w, [k]: e.target.value } : w)));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Please fill in your name and email");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        work_history: work.filter((w) => w.company || w.title || w.summary),
        education: edu.filter((e) => e.school || e.degree),
      };
      const { data } = await axios.post(
        `${BACKEND}/api/public/careers/${employerId}/jobs/${jobId}/apply-form`,
        payload
      );
      onSuccess?.(data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Application could not be submitted"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="online-application-form"
      className="space-y-6"
    >
      {/* Contact */}
      <fieldset className="grid gap-4 border border-slate-200 p-5 sm:grid-cols-2">
        <legend className="px-2 text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Contact
        </legend>
        <div className="sm:col-span-2">
          <Label>Full name *</Label>
          <Input data-testid="oa-name" value={form.full_name} onChange={update("full_name")} className="mt-1.5" required />
        </div>
        <div>
          <Label>Email *</Label>
          <Input data-testid="oa-email" type="email" value={form.email} onChange={update("email")} className="mt-1.5" required />
        </div>
        <div>
          <Label>Phone</Label>
          <Input data-testid="oa-phone" value={form.phone} onChange={update("phone")} className="mt-1.5" />
        </div>
        <div className="sm:col-span-2">
          <Label>Mailing address</Label>
          <Input data-testid="oa-address" value={form.address} onChange={update("address")} className="mt-1.5" placeholder="Street, City, State, ZIP" />
        </div>
      </fieldset>

      {/* Work history */}
      <fieldset className="border border-slate-200 p-5">
        <legend className="px-2 text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Work history
        </legend>
        <div className="space-y-4">
          {work.map((w, i) => (
            <div key={i} data-testid={`oa-work-${i}`} className="grid gap-3 border border-slate-100 p-3 sm:grid-cols-2">
              <Input placeholder="Company" data-testid={`oa-work-company-${i}`} value={w.company} onChange={updateWork(i, "company")} />
              <Input placeholder="Title" data-testid={`oa-work-title-${i}`} value={w.title} onChange={updateWork(i, "title")} />
              <Input placeholder="Start (e.g. Jun 2022)" data-testid={`oa-work-start-${i}`} value={w.start} onChange={updateWork(i, "start")} />
              <Input placeholder="End (e.g. Present)" data-testid={`oa-work-end-${i}`} value={w.end} onChange={updateWork(i, "end")} />
              <Textarea placeholder="Key responsibilities & achievements" data-testid={`oa-work-summary-${i}`} value={w.summary} onChange={updateWork(i, "summary")} className="sm:col-span-2" rows={3} />
              {work.length > 1 && (
                <button
                  type="button"
                  onClick={() => setWork((p) => p.filter((_, idx) => idx !== i))}
                  className="justify-self-end text-[10px] font-semibold uppercase tracking-[0.18em] text-rose-600 hover:underline sm:col-span-2"
                >
                  <Trash2 className="mr-1 inline h-3 w-3" /> Remove
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="oa-add-work"
          onClick={() => setWork((p) => [...p, emptyWork()])}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
        >
          <Plus className="h-3 w-3" /> Add another role
        </button>
      </fieldset>

      {/* Education */}
      <fieldset className="border border-slate-200 p-5">
        <legend className="px-2 text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Education
        </legend>
        <div className="space-y-3">
          {edu.map((ed, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-3">
              <Input placeholder="School" data-testid={`oa-edu-school-${i}`} value={ed.school} onChange={updateEdu(i, "school")} />
              <Input placeholder="Degree" data-testid={`oa-edu-degree-${i}`} value={ed.degree} onChange={updateEdu(i, "degree")} />
              <Input placeholder="Year" data-testid={`oa-edu-year-${i}`} value={ed.year} onChange={updateEdu(i, "year")} />
            </div>
          ))}
        </div>
        <button
          type="button"
          data-testid="oa-add-edu"
          onClick={() => setEdu((p) => [...p, emptyEdu()])}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline"
        >
          <Plus className="h-3 w-3" /> Add another
        </button>
      </fieldset>

      {/* Skills + free text */}
      <fieldset className="border border-slate-200 p-5">
        <legend className="px-2 text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Skills & fit
        </legend>
        <div className="space-y-4">
          <div>
            <Label>Top skills (comma separated)</Label>
            <Input data-testid="oa-skills" value={form.skills} onChange={update("skills")} className="mt-1.5" placeholder="Python, FastAPI, MongoDB, leadership…" />
          </div>
          <div>
            <Label>Why are you a good fit for this role?</Label>
            <Textarea data-testid="oa-why" value={form.why_good_fit} onChange={update("why_good_fit")} className="mt-1.5" rows={4} placeholder="Anything you want the hiring team to know" />
          </div>
        </div>
      </fieldset>

      {/* Logistics */}
      <fieldset className="grid gap-4 border border-slate-200 p-5 sm:grid-cols-2">
        <legend className="px-2 text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Logistics
        </legend>
        <div>
          <Label>Earliest start date</Label>
          <Input data-testid="oa-start" value={form.available_start} onChange={update("available_start")} className="mt-1.5" placeholder="e.g. 2 weeks from offer" />
        </div>
        <label className="flex items-end gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="oa-authorized"
            checked={form.authorized_to_work}
            onChange={(e) => setForm((p) => ({ ...p, authorized_to_work: e.target.checked }))}
            className="mb-1 h-4 w-4 accent-blue-700"
          />
          <span>I&apos;m authorized to work in this country</span>
        </label>
      </fieldset>

      <Button
        type="submit"
        data-testid="oa-submit"
        disabled={submitting}
        className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
      >
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
        Submit application
      </Button>
    </form>
  );
}
